export type PrimitiveType = 'string' | 'number' | 'boolean';
export type FieldSpec = [fieldName: string, type: PrimitiveType | FieldSpec[]];

export type ParseResult<T = Record<string, unknown>> = { data: T } | { error: string };

/**
 * Validates `raw` against a recursive field schema.
 * Returns `{ data }` on success, or `{ error }` with the first violation (including the full field path).
 */
export const parseBySchema = (raw: unknown, schema: FieldSpec[], path = ''): ParseResult => {
	if (!raw || typeof raw !== 'object') {
		return { error: `Expected an object at ${path || 'root'}, got: ${typeof raw}` };
	}

	const obj = raw as Record<string, unknown>;
	const result: Record<string, unknown> = {};

	for (const [name, type] of schema) {
		const fieldPath = `${path}${name}`;

		if (!(name in obj)) {
			return { error: `Missing required field: ${fieldPath}` };
		}

		const value = obj[name];

		if (Array.isArray(type)) {
			const nested = parseBySchema(value, type, `${fieldPath}.`);

			if ('error' in nested) {
				return nested;
			}

			result[name] = nested.data;
		} else {
			if (typeof value !== type) {
				return { error: `Invalid field ${fieldPath}: expected ${type}, got ${typeof value}` };
			}

			result[name] = value;
		}
	}

	return { data: result };
};
